import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { confermaTotp, iscriviTotp } from "@/lib/totp";
import { useAuth, MFA } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import AuthShell, { TestataModulo } from "@/components/auth/AuthShell";
import { CodiceSei } from "@/components/auth/CampiAccesso";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

/**
 * Attivazione del codice di verifica (TOTP), BLOCCANTE per il titolare al primo accesso.
 * Chiama supabase.auth.mfa.enroll() per ottenere QR + secret,
 * poi challenge() + verify() per attivare il fattore.
 */
export default function TotpEnrollmentPage() {
  const nav = useNavigate();
  const { session, profile, factors, mfaStato, refreshAal, signOut } = useAuth();
  const [enroll, setEnroll] = useState(null); // {id, totp:{qr_code, secret, uri}}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [enrollError, setEnrollError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  // Guardia contro la doppia esecuzione dell'effetto sotto React.StrictMode
  // (dev-only). StrictMode monta → smonta → rimonta il componente: il cleanup
  // con `cancelled` annulla l'USO del risultato ma NON la chiamata di rete,
  // quindi due enroll partono in parallelo con lo stesso friendly_name e la
  // seconda torna 500 unexpected_failure (collisione sull'indice unico
  // gestita male da Supabase). La useRef ci fa sopravvivere allo unmount.
  const enrollStartedRef = useRef(false);

  useEffect(() => {
    if (!session) {
      nav("/login", { replace: true });
      return;
    }
    // Solo il titolare deve fare l'enrollment forzato.
    if (profile && profile.ruolo !== "titolare") {
      nav("/", { replace: true }); // all'arrivo del ruolo
      return;
    }
    // Se ha già un fattore verificato, va avanti. Se non si sa, il cancello di «/» lo dice.
    if ((factors?.totp || []).some((f) => f.status === "verified") || mfaStato === MFA.NON_DISPONIBILE) {
      nav("/", { replace: true }); // all'arrivo del ruolo
      return;
    }
  }, [session, profile, factors, mfaStato, nav]);

  useEffect(() => {
    // Chi non deve iscriversi non deve nemmeno CHIEDERE un fattore.
    // `mfa.enroll()` scrive una riga in `auth.mfa_factors` nel momento in cui
    // parte: il reindirizzamento dell'effetto qui sopra arriva DOPO, e la riga
    // resta. Misurato: una singola visita a questa rotta da venditore gli ha
    // lasciato addosso un fattore `unverified` alle 08:42:10 — e il venditore
    // il secondo fattore non lo usa, quindi quella riga non se ne va più da
    // sola (la ripulitura del passo 1 gira solo per chi TORNA qui).
    //
    // Finché non si sa chi è, si esce SENZA alzare la guardia: alzarla su un
    // dato che non c'è ancora spegnerebbe l'iscrizione per sempre quando il
    // profilo arriva.
    if (!session || !profile) return;
    if (profile.ruolo !== "titolare") return;
    // Un fattore nuovo solo se si SA che non ne ha uno: con auth/v1/user caduta la lista
    // vuota non vuol dire niente (AuthContext, refreshAal).
    if (mfaStato !== MFA.DISPONIBILE) return;
    if ((factors?.totp || []).some((f) => f.status === "verified")) return;

    // Guardia: se un'esecuzione precedente ha già iniziato (StrictMode dev
    // rimonta i componenti), esci senza fare NIENTE. Non basta il cleanup
    // `cancelled` perché la chiamata di rete parte comunque.
    if (enrollStartedRef.current) return;
    enrollStartedRef.current = true;

    (async () => {
      try {
        // La pulizia dei fattori non confermati e il nuovo tentativo su un
        // conflitto stanno in lib/totp.js (li usa anche la pagina Sicurezza).
        const { data, error } = await iscriviTotp();

        // NOTA: nessun `cancelled` gate qui. Sotto React.StrictMode il primo
        // mount viene subito smontato e rimontato; se avessimo un flag
        // `cancelled` chiuso nella closure, lo unmount lo metterebbe a true
        // e la nostra setState verrebbe scartata anche se il secondo mount
        // NON rilancia il lavoro (fermato dalla useRef guard). Il risultato
        // sarebbe UI bloccata su «Generazione…» per sempre — regressione già
        // vista nell'iteration precedente. React 18 tollera setState su un
        // componente rimontato sotto StrictMode: lo stato si preserva.
        if (error) {
          setEnrollError(
            error.message ||
              "Impossibile generare il QR. Riprova o contatta il supporto."
          );
          return;
        }
        setEnroll(data);
      } catch (e) {
        setEnrollError(
          (e && e.message) ||
            "Errore imprevisto durante la generazione del QR. Riprova o contatta il supporto."
        );
      }
    })();
    // retryTick permette al bottone "Riprova" di rilanciare l'intera procedura
    // resettando la guardia enrollStartedRef prima di richiamare l'effetto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryTick, session, profile, factors, mfaStato]);

  const riprova = () => {
    setEnrollError(null);
    setEnroll(null);
    enrollStartedRef.current = false;
    setRetryTick((n) => n + 1);
  };

  const verify = async () => {
    if (!enroll) return;
    setBusy(true);
    const { error, codiceSbagliato } = await confermaTotp(enroll.id, code);
    setBusy(false);
    if (error) {
      toast.error(codiceSbagliato ? "Codice non valido, riprova" : error.message);
      return;
    }
    await refreshAal();
    toast.success("Codice di verifica attivato");
    // Si entra dal primo effetto, appena il contesto vede il fattore verificato: andare a «/» da qui, prima che la
    // rilettura arrivi, rimandava all'attivazione e faceva comparire un QR nuovo.
  };

  return (
    <AuthShell colonna="max-w-[460px]">
      <div className="flex flex-col gap-card">
        <TestataModulo titolo="Attiva il codice di verifica" icona={ShieldCheck}>
          Passaggio obbligatorio per il titolare. Ti serve un&#39;app di autenticazione sul telefono (Google Authenticator,
          1Password, Authy).
        </TestataModulo>

        <ol className="flex flex-col gap-card">
          <li>
            <p className="font-semibold text-ink">1. Inquadra il QR con l&#39;app di autenticazione</p>
            <p className="mt-micro text-small text-ink-2">Nell&#39;app premi «+» e scegli «Scansiona un codice QR». La voce si chiamerà Interni D&#39;Autore.</p>
            <div className="mt-inside flex min-h-[184px] justify-center rounded-lg border border-line bg-surface p-group sm:w-fit">
              {enroll?.totp?.qr_code ? (
                <img data-testid="totp-qr-code" src={enroll.totp.qr_code} alt="QR del codice di verifica" className="size-44" />
              ) : enrollError ? (
                <div role="alert" data-testid="totp-enroll-error" className="w-full max-w-xs rounded-md bg-danger-bg p-3 text-danger-ink">
                  <p className="font-semibold">Il QR non è arrivato.</p>
                  <p className="mt-1 break-words text-small">{enrollError}</p>
                  <Button type="button" variant="secondary" size="sm" className="mt-inside" onClick={riprova} data-testid="totp-retry-btn">
                    Riprova
                  </Button>
                </div>
              ) : (
                <div className="flex size-44 items-center justify-center text-small text-ink-2">Preparo il QR…</div>
              )}
            </div>
            {enroll?.totp?.secret ? (
              <p className="mt-inside text-small text-ink-2">
                Se non riesci a inquadrarlo, scrivi a mano la chiave:{" "}
                <span data-testid="totp-secret-key" className="select-all break-all font-mono text-ink">
                  {enroll.totp.secret}
                </span>
              </p>
            ) : null}
          </li>

          <li>
            <p className="font-semibold text-ink">2. Scrivi le 6 cifre che l&#39;app ti mostra</p>
            <div className="mt-inside flex" data-testid="totp-code-input">
              <CodiceSei value={code} onChange={setCode} aria-label="Codice a 6 cifre dell'app di autenticazione" />
            </div>
          </li>
        </ol>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <button
            type="button"
            onClick={signOut}
            data-testid="totp-cancel-btn"
            className="inline-flex min-h-11 items-center justify-center font-semibold text-brand-ink underline underline-offset-[3px] hover:decoration-2 md:min-h-0"
          >
            Annulla ed esci
          </button>
          <Button onClick={verify} size="xl" disabled={busy || code.length < 6} data-testid="totp-verify-btn" className="max-md:h-[54px] max-md:w-full">
            {busy ? "Verifica…" : "Attiva e accedi al gestionale"}
          </Button>
        </div>

        <p>
          <Link to="/aiuto/primo-accesso" className="inline-flex min-h-11 items-center font-semibold text-brand-ink underline underline-offset-[3px]" target="_blank" rel="noopener noreferrer" data-testid="aiuto-link">
            Come si attiva il codice, passo per passo
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
